import type { JiraChildWorkItem } from "../hooks/useJiraChildWorkItems";
import type { WorkflowFilterMode } from "../index";
import type { WorkflowInstanceRow, WorkflowTableRow } from "../types";

export type JiraChildState = { loading: boolean; items: JiraChildWorkItem[] | null };

function findCurrentStepTaskId(wf: WorkflowInstanceRow): string | undefined {
  const match = wf.taskSteps.find((s) => s.name === wf.currentStepName);
  if (match) return String(match.id);
  const inProgress = wf.taskSteps.find((s) => s.status === "IN_PROGRESS");
  return inProgress ? String(inProgress.id) : undefined;
}

function findCurrentStepName(wf: WorkflowInstanceRow): string | undefined {
  if (wf.currentStepName) return wf.currentStepName;
  const inProgress = wf.taskSteps.find((s) => s.status === "IN_PROGRESS");
  return inProgress?.name ?? undefined;
}

export function flattenWorkflowsToTreeRows(
  workflows: WorkflowInstanceRow[],
  jiraChildren: Map<string, JiraChildState> = new Map(),
): WorkflowTableRow[] {
  const rows: WorkflowTableRow[] = [];

  for (const wf of workflows) {
    const wfId = String(wf.id);
    const isJiraSource = wf.source === "JIRA";

    rows.push({
      id: wfId,
      path: [wfId],
      isParentRow: true,
      showIdSubtitle: true,
      displayName: wf.title ?? "",
      workflowId: wfId,
      templateName: wf.templateName ?? undefined,
      definitionScope: wf.definitionScope,
      definitionVersionNumber: wf.definitionVersionNumber,
      workflowType: wf.workflowType,
      clientName: wf.client?.name,
      clientId: wf.client?.id,
      entityName: wf.entity?.name,
      assignee: wf.currentAssignee ?? null,
      progress: wf.progress,
      currentStepName: findCurrentStepName(wf),
      currentStepTaskId: findCurrentStepTaskId(wf),
      createdAt: wf.createdAt ?? undefined,
      slaStatus: wf.slaStatus,
      dueDate: wf.dueDate,
      status: wf.status,
      updatedAt: wf.updatedAt ?? null,
      source: wf.source ?? null,
      externalRefId: wf.externalRefId ?? null,
      faPreparer: wf.faPreparer ?? null,
      faReviewer: wf.faReviewer ?? null,
      isPreparer: wf.isPreparer ?? null,
      isReviewer: wf.isReviewer ?? null,
      requestedBy: wf.requestedBy ?? null,
      statusCategory: wf.statusCategory ?? null,
    });

    if (isJiraSource) {
      const state = jiraChildren.get(wfId);
      if (!state) {
        rows.push({
          id: `${wfId}-jira-placeholder`,
          path: [wfId, `${wfId}-jira-placeholder`],
          isParentRow: false,
          displayName: "",
          isJiraPlaceholder: true,
        });
      } else if (state.loading) {
        rows.push({
          id: `${wfId}-jira-loading`,
          path: [wfId, `${wfId}-jira-loading`],
          isParentRow: false,
          displayName: "Loading tasks…",
          isJiraLoading: true,
        });
      } else if (state.items && state.items.length > 0) {
        for (const item of state.items) {
          rows.push({
            id: `${wfId}-jira-${item.id}`,
            path: [wfId, `${wfId}-jira-${item.id}`],
            isParentRow: false,
            displayName: `${item.key} — ${item.summary}`,
            assignee: item.assignedTo ? { id: item.id, displayName: item.assignedTo } : null,
            status: item.status,
          });
        }
      }
    } else {
      for (const step of wf.taskSteps) {
        const rawTaskId = String(step.id);
        rows.push({
          id: `${wfId}-task-${rawTaskId}`,
          path: [wfId, `${wfId}-task-${rawTaskId}`],
          isParentRow: false,
          rawTaskId,
          displayName: step.name,
          assignee: step.assignee ?? null,
          dueDate: step.dueDate ?? null,
          status: step.status,
          gpxStatus: step.gpxStatus ?? null,
          stepOrder: step.stepOrder,
          taskType: step.taskType ?? undefined,
        });
      }
    }
  }

  return rows;
}

export function flattenWorkflowsParentOnly(
  workflows: WorkflowInstanceRow[],
): WorkflowTableRow[] {
  return workflows.map((wf) => ({
    id: String(wf.id),
    path: [String(wf.id)],
    isParentRow: true,
    displayName: wf.title ?? "",
    workflowId: String(wf.id),
    templateName: wf.templateName ?? undefined,
    definitionScope: wf.definitionScope,
    definitionVersionNumber: wf.definitionVersionNumber,
    workflowType: wf.workflowType,
    clientName: wf.client?.name,
    clientId: wf.client?.id,
    entityName: wf.entity?.name,
    assignee: wf.currentAssignee ?? null,
    progress: wf.progress,
    currentStepName: findCurrentStepName(wf),
    currentStepTaskId: findCurrentStepTaskId(wf),
    createdAt: wf.createdAt ?? undefined,
    slaStatus: wf.slaStatus,
    dueDate: wf.dueDate,
    status: wf.status,
    updatedAt: wf.updatedAt ?? null,
    source: wf.source ?? null,
    externalRefId: wf.externalRefId ?? null,
    faPreparer: wf.faPreparer ?? null,
    faReviewer: wf.faReviewer ?? null,
    isPreparer: wf.isPreparer ?? null,
    isReviewer: wf.isReviewer ?? null,
    requestedBy: wf.requestedBy ?? null,
    statusCategory: wf.statusCategory ?? null,
  }));
}

// TODO: BE currently owns the assigned-to-me / all-open split via query variables.
// Keep the FE signature in place so future client-side refinements (e.g. mode-
// specific sorting) can be added without changing call sites.
export function filterWorkflowsByMode(
  workflows: WorkflowInstanceRow[],
  filterMode: WorkflowFilterMode,
): WorkflowInstanceRow[] {
  void filterMode;
  return workflows;
}
